import { addActor, rand, spawn } from '../engine';
import { ease } from '../kit';
import { glow, glowSprite, twinkle } from '../shapes';
import { TAU } from './types';

/** Set pieces shared by several presets: a glowing serpent (koi-dragon, the carp's dragon), sun rays. */

export interface SerpentOpts {
  dur: number;
  delay?: number;
  /** head position at k = 0..1 (CSS px) */
  path: (k: number, W: number, H: number) => { x: number; y: number };
  /** body colours: head → tail */
  body: readonly [string, string];
  /** glow colour (#RRGGBB) */
  aura: string;
  /** half-thickness of the head, px */
  width: number;
  /** number of segments (body length in path samples) */
  segments?: number;
  /** gold glints trailing behind */
  trail?: string;
  /** whiskers and a crest (a dragon), or a plain fish tail */
  dragon?: boolean;
}

/** A long glowing body that follows its head along a path; drawn as tapered discs with a light core. */
export function serpent(o: SerpentOpts): void {
  const hist: { x: number; y: number }[] = [];
  const segs = o.segments ?? 30;
  let acc = 0;
  addActor({
    dur: o.dur,
    delay: o.delay ?? 0,
    front: true,
    draw(g, k, f) {
      const h = o.path(ease.inOut(k), f.W, f.H);
      // the body is sampled by distance (not by frame), so its length does not depend on the frame rate
      const h0 = hist[0];
      if (!h0 || Math.hypot(h.x - h0.x, h.y - h0.y) >= o.width * 0.4) hist.unshift(h);
      else hist[0] = h;
      if (hist.length > segs * 2) hist.length = segs * 2;
      const a = ease.env(k, 0.08, 0.86);
      const sp = glowSprite(o.aura);
      g.globalCompositeOperation = 'lighter';
      // aura
      if (sp)
        for (let i = 0; i < hist.length; i += 4) {
          const q = hist[i];
          if (!q) continue;
          const r = o.width * 3.2 * (1 - i / (hist.length + 4));
          g.globalAlpha = 0.28 * a;
          g.drawImage(sp, q.x - r, q.y - r, r * 2, r * 2);
        }
      g.globalCompositeOperation = 'source-over';
      // body: a tapered ribbon, tail first, then a light stripe and scales along the back
      const radius = (i: number) =>
        o.width * (1 - (i / (segs * 2)) * 0.85) * (0.9 + 0.1 * Math.sin(i * 0.7 - f.t * 10));
      g.lineCap = 'round';
      for (const pass of [0, 1] as const) {
        for (let i = hist.length - 1; i > 0; i--) {
          const q = hist[i];
          const n = hist[i - 1];
          if (!q || !n) continue;
          const u = i / (segs * 2);
          const r = radius(i);
          g.globalAlpha = pass ? a * 0.32 * (1 - u) : a * (1 - u * 0.35);
          g.strokeStyle = pass ? '#FFFFFF' : u < 0.5 ? o.body[0] : o.body[1];
          g.lineWidth = Math.max(1, pass ? r * 0.5 : r * 2);
          g.beginPath();
          g.moveTo(q.x, q.y);
          g.lineTo(n.x, n.y);
          g.stroke();
        }
      }
      g.fillStyle = o.body[1];
      for (let i = hist.length - 2; i > 2; i -= 3) {
        const q = hist[i];
        const n = hist[i - 1];
        if (!q || !n) continue;
        const r = radius(i);
        const d = Math.atan2(n.y - q.y, n.x - q.x) - Math.PI / 2;
        g.globalAlpha = a * 0.75;
        g.beginPath();
        g.arc(q.x + Math.cos(d) * r * 0.55, q.y + Math.sin(d) * r * 0.55, Math.max(0.5, r * 0.32), 0, TAU);
        g.fill();
      }
      // head (seen from above): snout forward, antlers and whiskers flowing back
      const n = hist[1] ?? hist[0] ?? h;
      const dir = Math.atan2(h.y - n.y, h.x - n.x);
      const w = o.width * 1.35;
      g.globalAlpha = a;
      g.save();
      g.translate(h.x, h.y);
      g.rotate(dir);
      if (o.dragon) {
        const wv = Math.sin(f.t * 5) * w * 0.5;
        g.strokeStyle = o.body[1];
        g.lineCap = 'round';
        g.lineWidth = Math.max(1, w * 0.16);
        // antlers
        g.beginPath();
        for (const sd of [-1, 1]) {
          g.moveTo(-w * 0.1, sd * w * 0.55);
          g.quadraticCurveTo(-w * 0.9, sd * w * 1.3, -w * 1.9, sd * w * 1.1);
          g.moveTo(-w * 0.9, sd * w * 1.15);
          g.lineTo(-w * 1.1, sd * w * 1.7);
        }
        g.stroke();
        // whiskers
        g.strokeStyle = o.body[0];
        g.lineWidth = Math.max(1, w * 0.09);
        g.beginPath();
        for (const sd of [-1, 1]) {
          g.moveTo(w * 1.7, sd * w * 0.3);
          g.bezierCurveTo(
            w * 1.2,
            sd * w * 1.4,
            -w * 0.6,
            sd * (w * 1.2 + wv),
            -w * 2.4,
            sd * (w * 1.6 - wv),
          );
        }
        g.stroke();
      }
      // skull and snout
      g.fillStyle = o.body[0];
      g.beginPath();
      g.ellipse(w * 0.25, 0, w * 1.05, w * 0.9, 0, 0, TAU);
      g.fill();
      g.beginPath();
      g.ellipse(w * 1.15, 0, w * 0.75, w * 0.55, 0, 0, TAU);
      g.fill();
      g.fillStyle = '#FFFFFF';
      g.globalAlpha = a * 0.35;
      g.beginPath();
      g.ellipse(w * 0.4, 0, w * 0.75, w * 0.22, 0, 0, TAU);
      g.fill();
      g.globalAlpha = a;
      // nostrils and eyes
      g.fillStyle = '#5A3418';
      g.beginPath();
      g.arc(w * 1.65, -w * 0.22, w * 0.08, 0, TAU);
      g.arc(w * 1.65, w * 0.22, w * 0.08, 0, TAU);
      g.fill();
      for (const sd of [-1, 1]) {
        g.fillStyle = '#FFFFFF';
        g.beginPath();
        g.ellipse(w * 0.55, sd * w * 0.5, w * 0.2, w * 0.13, sd * 0.3, 0, TAU);
        g.fill();
        g.fillStyle = '#1A1206';
        g.beginPath();
        g.arc(w * 0.6, sd * w * 0.5, w * 0.08, 0, TAU);
        g.fill();
      }
      g.restore();
      // trailing glints
      if (o.trail && k < 0.92) {
        acc += f.dt * 40;
        while (acc >= 1) {
          acc -= 1;
          const q = hist[Math.floor(rand() * Math.min(hist.length, segs))] ?? h;
          spawn({
            x: q.x + (rand() - 0.5) * o.width,
            y: q.y + (rand() - 0.5) * o.width,
            vx: (rand() - 0.5) * 30,
            vy: 20 + rand() * 40,
            draw: rand() < 0.5 ? twinkle : glow,
            col: o.trail,
            size: 3 + rand() * 4,
            life: 0.6 + rand() * 0.5,
            vr: (rand() - 0.5) * 4,
            add: true,
            fadeIn: 0.2,
          });
        }
      }
    },
  });
}

/** Rotating light rays behind a set piece. */
export function rays(o: {
  x: (W: number) => number;
  y: (H: number) => number;
  r: (W: number, H: number) => number;
  cols: readonly string[];
  n: number;
  dur: number;
  delay?: number;
  alpha?: number;
  spin?: number;
}): void {
  addActor({
    dur: o.dur,
    delay: o.delay ?? 0,
    draw(g, k, f) {
      const a = ease.env(k, 0.15, 0.7) * (o.alpha ?? 0.22);
      const cx = o.x(f.W);
      const cy = o.y(f.H);
      const R = o.r(f.W, f.H) * (0.6 + 0.4 * ease.out(Math.min(1, k * 3)));
      const w = (Math.PI / o.n) * 0.55;
      g.globalCompositeOperation = 'lighter';
      const fades = o.cols.map((col) => {
        const gr =
          typeof g.createRadialGradient === 'function' ? g.createRadialGradient(cx, cy, 0, cx, cy, R) : null;
        if (!gr || typeof gr.addColorStop !== 'function') return col;
        gr.addColorStop(0, col);
        gr.addColorStop(0.55, col + '88');
        gr.addColorStop(1, col + '00');
        return gr;
      });
      for (let i = 0; i < o.n; i++) {
        const an = (i / o.n) * TAU + f.t * (o.spin ?? 0.35);
        g.globalAlpha = a;
        g.fillStyle = fades[i % fades.length] ?? '#FFD166';
        g.beginPath();
        g.moveTo(cx, cy);
        g.arc(cx, cy, R, an - w, an + w);
        g.closePath();
        g.fill();
      }
      const sp = glowSprite(o.cols[0] ?? '#FFD166');
      if (sp) {
        g.globalAlpha = a * 2.5;
        const r = R * 0.45;
        g.drawImage(sp, cx - r, cy - r, r * 2, r * 2);
      }
    },
  });
}
